import { createContext, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ConnectionState, RealtimeClient, RunEventHandlers } from './client';
import type { RunState } from './run-store';

/**
 * Realtime, for components (P0-WEB-05).
 *
 * Optional by design — `useRealtime` returns `undefined` outside a provider
 * rather than throwing, unlike `useApiClient`. Realtime is an accelerator and
 * never the source of truth, so a component that renders without it should
 * still render: everything it shows arrived over HTTP first. A hard throw here
 * would make a socket the portal can live without into a reason it cannot
 * paint.
 */
export const RealtimeContext = createContext<RealtimeClient | undefined>(undefined);

export function useRealtime(): RealtimeClient | undefined {
  return useContext(RealtimeContext);
}

/** `connecting` when there is no client at all — nothing is live yet, which is
 *  the honest reading and not an error state. */
export function useConnectionState(): ConnectionState {
  const client = useRealtime();

  return useSyncExternalStore(
    (listener) => client?.connection.subscribe(listener) ?? noop,
    () => client?.connection.getSnapshot() ?? 'connecting',
    () => 'connecting' as const,
  );
}

/**
 * Subscribes to an organisation's runs for as long as the component is mounted,
 * and returns the live map.
 *
 * The contract's rule "subscribe before submitting a run — the queued broadcast
 * can beat the HTTP response that returns the run id" is why this takes an
 * organisation rather than a run id: the subscription is already open before
 * anything is submitted, so there is no window to lose an event in.
 */
export function useOrganisationRuns(
  organisationId: string | undefined,
  handlers?: RunEventHandlers,
): ReadonlyMap<string, RunState> {
  const client = useRealtime();

  // A ref rather than a dependency. Callers pass an object literal, so
  // depending on `handlers` would unsubscribe and resubscribe on every render —
  // and every resubscription is a round trip to the channel auth endpoint.
  const latest = useRef(handlers);
  useEffect(() => {
    latest.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (client === undefined || organisationId === undefined) return;

    return client.subscribeToOrganisationRuns(organisationId, {
      onRun: (payload) => latest.current?.onRun?.(payload),
    });
  }, [client, organisationId]);

  return useSyncExternalStore(
    (listener) =>
      client?.runs.subscribe(() => {
        listener();
      }) ?? noop,
    () => client?.runs.getSnapshot() ?? EMPTY,
    () => EMPTY,
  );
}

const EMPTY: ReadonlyMap<string, RunState> = new Map();

function noop(): void {
  // Nothing to unsubscribe from.
}
